import React from 'react';
import { Tv } from 'lucide-react';
import { ABOUT_TV_VIDEO_URL } from '../../constants/site';
import { useJsonLdScript } from '../../hooks/useJsonLdScript';

const VIDEO_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'VideoObject',
  name: 'Virginia Beach Oceanfront Living — American Dream TV with Jessica Burnette',
  description:
    'Realtor Jessica Burnette tours oceanfront living in Virginia Beach on American Dream TV — ' +
    'from the historic boardwalk and fishing pier to a luxury oceanfront condo at 921 Atlantic Avenue. ' +
    'See why coastal Virginia & North Carolina buyers and military families call Jess and forget the rest.',
  thumbnailUrl: ['https://i.ytimg.com/vi/1DYjDJviqfY/maxresdefault.jpg'],
  uploadDate: '2025-05-08',
  duration: 'PT5M43S',
  contentUrl: 'https://www.youtube.com/watch?v=1DYjDJviqfY',
  embedUrl: 'https://www.youtube.com/embed/1DYjDJviqfY',
  publisher: { '@type': 'Organization', name: 'Jess Sells East Coast' },
};

const TvIntro = () => (
  <div className="text-center max-w-3xl mx-auto mb-10 space-y-5">
    <div className="inline-flex items-center space-x-3 px-6 py-3 bg-white/10 backdrop-blur-sm border-2 border-gold-400/50 rounded-full">
      <Tv className="w-5 h-5 text-gold-400" />
      <span className="text-sm font-bold text-white tracking-wide uppercase">As Seen on American Dream TV</span>
    </div>
    <h2 className="text-4xl sm:text-5xl font-serif font-bold text-white leading-tight">
      Virginia Beach Oceanfront Living
      <span className="block gold-gradient-text">with Realtor Jessica Burnette</span>
    </h2>
    <p className="text-lg text-white/80 leading-relaxed">
      Take a video tour of oceanfront living in Virginia Beach with Jessica Burnette, your Coastal Virginia &amp; North Carolina real estate expert. From the iconic boardwalk and fishing pier to a luxury oceanfront condo, discover the East Coast beach lifestyle that military families and luxury buyers trust Jess to help them find.
    </p>
  </div>
);

const TvVideoEmbed = () => (
  <div className="relative max-w-4xl mx-auto">
    <div className="absolute -inset-2 bg-gradient-to-r from-gold-400/40 to-orange-400/40 blur-xl rounded-3xl opacity-60"></div>
    <div className="relative rounded-2xl overflow-hidden shadow-2xl border-2 border-gold-400/30" style={{ paddingBottom: '56.25%', height: 0 }}>
      <iframe
        data-testid="as-seen-on-tv-video"
        src={ABOUT_TV_VIDEO_URL}
        title="Virginia Beach Oceanfront Living on American Dream TV with Realtor Jessica Burnette"
        className="absolute top-0 left-0 w-full h-full"
        style={{ border: 0 }}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
        loading="lazy"
      />
    </div>
    <p className="text-center text-sm text-white/60 mt-4">Featured on American Dream TV &bull; Hosted by Jessica Burnette &bull; Serving Coastal VA &amp; NC</p>
  </div>
);

export const AsSeenOnTVSection = () => {
  useJsonLdScript('tv-video-schema', VIDEO_SCHEMA);
  return (
    <section id="as-seen-on-tv" data-testid="as-seen-on-tv-section" className="py-16 sm:py-24 bg-gradient-to-br from-[#143160] to-[#1E4A86] relative overflow-hidden">
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <div className="absolute top-10 right-10 w-64 h-64 bg-gold-400 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 left-10 w-48 h-48 bg-cyan-400 rounded-full blur-3xl"></div>
      </div>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <TvIntro />
        <TvVideoEmbed />
      </div>
    </section>
  );
};
